/**
 * Product Customizer - Shopify Integration
 * Connects to Factory-Chic configurator API
 */
class ProductCustomizer {
  constructor(container) {
    this.container = container;
    this.config = null;
    this.predesigns = [];
    this.currentPredesign = null;
    this.predesignData = null;
    this.currentColor = null;
    this.currentSize = null;
    this.currentPicto = null;
    this.userHasInteractedWithPicto = false;
    this.quantity = 1;
    this.debounceTimer = null;
    this.savedUserInputs = {};
    this.isFirstLoad = true;
    this.draggedElement = null;
    this.colorThumbnails = {};

    // Default texts loaded from predesign config/API (no hardcoded values)
    this.defaultTexts = {};
    this.thumbnailsLoaded = false;
    this.currentLang = this.detectLanguage();
    this.colorStaticImages = {};
    this.baseColorImages = {};

    this.init();
  }

  async init() {
    try {
      this.loadConfig();
      this.setupElements();
      this.bindEvents();
      this.initColorsFromLiquid();
      await this.checkUrlParams();
      await this.loadPredesigns();
      this.loadBannedWords();
      this.filterColorsByStock();
      this.initGuaranteeSlider();

      // Weglot integration: detect language and listen for changes
      this.initWeglot();
    } catch (e) {
      console.error('[Customizer] init() failed:', e);
    }
  }

  async filterColorsByStock() {
    if (!this.elements.colorsGrid || !this.config?.productLegacyId) return;
    try {
      const res = await fetch(
        `${this.config.apiEndpoint}/api/stock/public/${this.config.productLegacyId}`
      );
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !Array.isArray(data.stocks)) return;

      // Store raw stock data for re-filtering on size change
      this._stockData = data.stocks;
      this._buildStockMap();

      // Mark out-of-stock colors visually
      const items = this.elements.colorsGrid.querySelectorAll(
        '.product-customizer__color-item'
      );
      items.forEach((item) => {
        const isOos = this.isColorOutOfStock(item.dataset.color);
        item.classList.toggle('product-customizer__color-item--oos', isOos);
        item.style.opacity = isOos ? '0.35' : '';
        item.style.pointerEvents = isOos ? 'none' : '';
      });

      // Check current selection
      const activeItem = this.elements.colorsGrid.querySelector(
        '.product-customizer__color-item--active'
      );
      if (activeItem) {
        this.updateAddToCartFromStock(activeItem.dataset.color);
      }

      // If active color is OOS, select first available
      if (activeItem && this.isColorOutOfStock(activeItem.dataset.color)) {
        const firstAvailable = Array.from(items).find(
          (el) => !this.isColorOutOfStock(el.dataset.color)
        );
        if (firstAvailable) {
          this.selectColor(firstAvailable);
        } else {
          this.setOutOfStock(true);
        }
      }
    } catch (err) {
      console.warn('[Customizer] Stock fetch failed:', err);
    }
  }

  loadBannedWords() {
    this.bannedWords = [];
    fetch(`${this.config.apiEndpoint}/api/banned-words/list`)
      .then(r => r.json())
      .then(d => { this.bannedWords = (d.words || []).map(w => typeof w === 'string' ? w : w.word || w); })
      .catch(() => {});
  }

  validateText(text) {
    if (!text || !this.bannedWords || !this.bannedWords.length) return { valid: true };
    const normalized = text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const tokens = normalized.split(/[^a-z0-9]+/).filter(Boolean);
    for (const word of this.bannedWords) {
      const normalizedWord = word.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      if (tokens.includes(normalizedWord)) {
        return { valid: false, word };
      }
    }
    return { valid: true };
  }

  showInputError(input, message) {
    input.classList.add('product-customizer__word-input--error');
    let errorEl = input.parentElement.querySelector('.product-customizer__word-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'product-customizer__word-error';
      input.parentElement.appendChild(errorEl);
    }
    errorEl.textContent = message;
    errorEl.style.display = 'none';
  }

  clearInputError(input) {
    input.classList.remove('product-customizer__word-input--error');
    const errorEl = input.parentElement.querySelector('.product-customizer__word-error');
    if (errorEl) errorEl.style.display = 'none';
  }

  initColorsFromLiquid() {
    if (!this.elements.colorsGrid) return;

    // Bind click events to Liquid-rendered color items
    this.elements.colorsGrid.querySelectorAll('.product-customizer__color-item').forEach(item => {
      item.addEventListener('click', () => this.selectColor(item));
    });

    // Sync initial color from URL variant param
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    if (urlVariantId) {
      const matchingItem = this.elements.colorsGrid.querySelector(`[data-variant-id="${urlVariantId}"]`);
      if (matchingItem) {
        this.elements.colorsGrid.querySelectorAll('.product-customizer__color-item').forEach(el => {
          el.classList.remove('product-customizer__color-item--active');
        });
        matchingItem.classList.add('product-customizer__color-item--active');
      }
    }

    // Initialize current color from active item
    const activeColor = this.elements.colorsGrid.querySelector('.product-customizer__color-item--active');
    if (activeColor) {
      this.currentColor = activeColor.dataset.color;
      this.updateColorSummary(this.currentColor);
      this.updateCurrentColorIndicator(activeColor);
      this.updateVariantFromOptions();
    }

    // Listen for color changes from main product
    document.addEventListener('variant:changed', (e) => {
      if (e.detail.source === 'customizer') return;
      const color = e.detail.color;
      if (!color || !this.elements.colorsGrid) return;
      const matchingItem = this.elements.colorsGrid.querySelector(`[data-color="${color}"]`);
      if (matchingItem && !matchingItem.classList.contains('product-customizer__color-item--active')) {
        this.selectColor(matchingItem, false);
      }
    });
  }

  loadConfig() {
    const configEl = document.getElementById('customizer-config');
    if (configEl) {
      try {
        this.config = JSON.parse(configEl.textContent);
        const isLocal = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
        // Use same hostname as current page to avoid cross-origin issues
        let localEndpoint = this.config.apiEndpointLocal || 'http://localhost:3992';
        if (isLocal && window.location.hostname === '127.0.0.1') {
          localEndpoint = localEndpoint.replace('localhost', '127.0.0.1');
        }
        this.config.apiEndpoint = isLocal ? localEndpoint : this.config.apiEndpointProd;
        if (this.config.defaultSize) {
          this.currentSize = this.config.defaultSize;
        }
      } catch (e) {
        console.error('Config parse error:', e);
      }
    }
  }

  setupElements() {
    this.elements = {
      previewImage: document.getElementById('customizer-preview-image'),
      previewPlaceholder: document.getElementById('customizer-preview-placeholder'),
      previewLoading: document.getElementById('customizer-preview-loading'),
      colorsGrid: document.getElementById('customizer-colors'),
      designsGrid: document.getElementById('customizer-designs'),
      wordsContainer: document.getElementById('customizer-words'),
      pictosGrid: document.getElementById('customizer-pictos'),
      btnClearWords: document.getElementById('btn-clear-words'),
      btnNoPicto: document.getElementById('btn-no-picto'),
      btnAddToCart: document.getElementById('btn-add-to-cart'),
      btnShare: document.getElementById('btn-share'),
      btnSave: document.getElementById('btn-save'),
      btnPreviewShare: document.getElementById('btn-preview-share'),
      btnPreviewSave: document.getElementById('btn-preview-save'),
      btnPreviewZoom: document.getElementById('btn-preview-zoom'),
      totalPrice: document.getElementById('total-price'),
      quantityValue: document.getElementById('quantity-value'),
      quantitySelect: document.getElementById('quantity-select'),
      summarySize: document.getElementById('summary-size'),
      summaryColor: document.getElementById('summary-color'),
      summaryTypo: document.getElementById('summary-typo'),
      deliveryDate: document.getElementById('delivery-date'),
      currentColorIndicator: document.getElementById('current-color-indicator'),
      shareModal: document.getElementById('share-modal'),
      shareModalClose: document.getElementById('share-modal-close'),
      shareModalForm: document.getElementById('share-modal-form'),
      shareModalSuccess: document.getElementById('share-modal-success'),
      shareModalMsg: document.getElementById('share-modal-msg'),
      shareSubmit: document.getElementById('share-submit'),
      shareCancel: document.getElementById('share-cancel'),
      shareCloseSuccess: document.getElementById('share-close-success'),
      shareEmailTo: document.getElementById('partage_email'),
      shareEmailFrom: document.getElementById('email_from'),
      shareCopy: document.getElementById('share_copy'),
      shareMessage: document.getElementById('share_message')
    };
  }

  bindEvents() {
    this.container.querySelectorAll('.product-customizer__qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e));
    });

    if (this.elements.quantitySelect) {
      this.elements.quantitySelect.addEventListener('change', (e) => this.handleQuantitySelect(e));
    }

    this.container.querySelectorAll('.product-customizer__size-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleSizeChange(e));
    });

    if (this.elements.btnClearWords) {
      this.elements.btnClearWords.addEventListener('click', () => this.clearWords());
    }

    if (this.elements.btnNoPicto) {
      this.elements.btnNoPicto.addEventListener('click', () => this.clearPicto());
    }

    if (this.elements.btnAddToCart) {
      this.elements.btnAddToCart.addEventListener('click', () => this.addToCart());
    }

    if (this.elements.btnShare) {
      this.elements.btnShare.addEventListener('click', () => this.shareCreation());
    }

    if (this.elements.btnSave) {
      this.elements.btnSave.addEventListener('click', () => this.saveCreation());
    }

    if (this.elements.btnPreviewShare) {
      this.elements.btnPreviewShare.addEventListener('click', () => this.shareCreation());
    }

    if (this.elements.btnPreviewSave) {
      this.elements.btnPreviewSave.addEventListener('click', () => this.saveCreation());
    }

    if (this.elements.shareModalClose) {
      this.elements.shareModalClose.addEventListener('click', () => this.closeShareModal());
    }
    if (this.elements.shareCancel) {
      this.elements.shareCancel.addEventListener('click', (e) => { e.preventDefault(); this.closeShareModal(); });
    }
    if (this.elements.shareSubmit) {
      this.elements.shareSubmit.addEventListener('click', () => this.submitShareForm());
    }
    if (this.elements.shareCloseSuccess) {
      this.elements.shareCloseSuccess.addEventListener('click', () => this.closeShareModal());
    }
    if (this.elements.shareModal) {
      this.elements.shareModal.addEventListener('click', (e) => {
        if (e.target === this.elements.shareModal) this.closeShareModal();
      });
    }

    if (this.elements.btnPreviewZoom) {
      this.elements.btnPreviewZoom.addEventListener('click', () => this.openZoomModal());
    }
    if (this.elements.previewImage) {
      this.elements.previewImage.addEventListener('click', () => this.openZoomModal());
    }
  }

  // Save current user inputs before switching design
  saveUserInputs() {
    this.saveWordsOrder();

    // Get current style defaults to detect if user actually modified texts
    // Map custom defaults by position (IDs differ across styles)
    const predesign = this.predesigns.find(p => p.filename === this.currentPredesign);
    const customDefaults = this.getLocalizedDefaultTexts();
    const metaDefaults = predesign?.defaultTexts || {};
    const legacyDefaults = this.defaultTexts[this.currentPredesign] || {};
    const customTextValues = Object.entries(customDefaults)
      .filter(([k]) => !k.startsWith('picto'))
      .map(([, v]) => v);
    const defaults = {};
    if (customTextValues.length > 0) {
      const inputs = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input');
      inputs.forEach((input, i) => {
        defaults[input.name] = i < customTextValues.length ? customTextValues[i] : (metaDefaults[input.name] || '');
      });
    } else {
      Object.assign(defaults, legacyDefaults, metaDefaults);
    }

    let userModified = false;
    this.savedUserInputs = {};
    this.savedUserInputsByPosition = [];
    this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input').forEach((input, index) => {
      if (input.value.trim()) {
        this.savedUserInputs[input.name] = input.value;
        this.savedUserInputsByPosition[index] = input.value;
        if (input.value !== (defaults[input.name] || '')) {
          userModified = true;
        }
      }
    });

    // Detect reordering as a modification (drag-and-drop changes position, not values)
    if (!userModified && predesign?.lines) {
      const originalTextIds = predesign.lines.filter(l => l.type === 'text').map(l => l.id);
      const currentInputIds = Array.from(
        this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input')
      ).map(i => i.name);
      if (originalTextIds.some((id, i) => id !== currentInputIds[i])) {
        userModified = true;
      }
    }

    // If texts match defaults (user didn't modify), clear so new style gets its own defaults
    if (!userModified) {
      this.savedUserInputs = {};
      this.savedUserInputsByPosition = [];
    }
  }

  // Restore user inputs after switching design
  restoreUserInputs() {
    if (!this.savedUserInputsByPosition || this.savedUserInputsByPosition.length === 0) return;

    // Restore by position only — IDs can differ AND reorder across styles
    const inputs = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input');
    inputs.forEach((input, index) => {
      if (this.savedUserInputsByPosition[index]) {
        input.value = this.savedUserInputsByPosition[index];
      }
    });
    this.updateClearButton();
  }

  async loadPredesigns() {
    if (!this.config) return;

    // Load predesigns from metaobject config (inline JSON)
    const predesignsData = this.config.predesigns?.predesigns || [];

    if (predesignsData.length > 0) {
      this.predesigns = predesignsData.map(p => ({
        filename: p.file,
        number: p.number,
        name: p.name,
        preview: p.preview,
        lines: p.lines,
        combinaisons: p.combinaisons || [],
        defaultTexts: p.defaultTexts || {}
      }));

      // Enrich predesigns from API (DB is source of truth for lignes, combinaisons, defaultTexts)
      if (this.config.productLegacyId && this.config.variantLegacyId) {
        const endpoint = this.config.apiEndpoint;
        await Promise.all(this.predesigns.map(async (p) => {
          try {
            const res = await fetch(`${endpoint}/api/predesign/load`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                filename: p.filename,
                id_product: this.config.productLegacyId,
                id_product_attribute: this.config.variantLegacyId
              })
            });
            const data = await res.json();
            if (data.data) {
              if (data.data.lignes) p.lines = data.data.lignes;
              if (data.data.combinaisons) p.combinaisons = data.data.combinaisons;
              if (data.data.defaultTexts) p.defaultTexts = data.data.defaultTexts;
            }
          } catch { /* keep metaobject data as fallback */ }
        }));
      }
    } else if (this.config.loadFromApi && this.config.productLegacyId && this.config.variantLegacyId) {
      // Load predesigns from configurateur API (new direct flow)
      try {
        const endpoint = this.config.apiEndpoint;
        const res = await fetch(`${endpoint}/api/predesign/list?id_product=${this.config.productLegacyId}&id_product_attribute=${this.config.variantLegacyId}`);
        const data = await res.json();
        if (data.success && data.predesigns && data.predesigns.length > 0) {
          // Load full config for each predesign to get lignes, combinaisons, defaultTexts
          const predesignConfigs = await Promise.all(
            data.predesigns.map(async (p) => {
              try {
                const cfgRes = await fetch(`${endpoint}/api/predesign/load`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    filename: p.filename,
                    id_product: this.config.productLegacyId,
                    id_product_attribute: this.config.variantLegacyId
                  })
                });
                const cfgData = await cfgRes.json();
                return cfgData.data || null;
              } catch { return null; }
            })
          );

          this.predesigns = data.predesigns.map((p, i) => {
            const cfg = predesignConfigs[i];
            const allLignes = cfg?.lignes || [];
            const nblignes = cfg?.nblignes || 0;
            const nbpictos = cfg?.nbpictos || 0;

            // Filter visible lines: if nblignes is set, only show that many text lines + nbpictos pictos
            let lignes = allLignes;
            if (nblignes > 0 || nbpictos > 0) {
              const textLines = allLignes.filter(l => l.type === 'text');
              const pictoLines = allLignes.filter(l => l.type === 'picto');
              const visibleText = nblignes > 0 ? textLines.slice(0, nblignes) : textLines;
              const visiblePicto = nbpictos > 0 ? pictoLines.slice(0, nbpictos) : pictoLines;
              lignes = [...visibleText, ...visiblePicto].sort((a, b) => a.num - b.num);
            }

            // Custom defaults are stored with one style's IDs — map by position across styles
            const customDefaults = this.getLocalizedDefaultTexts();
            const apiDefaults = cfg?.defaultTexts || {};
            const defaultTexts = {};
            const textLignes = lignes.filter(l => l.type === 'text');
            const customTextValues = Object.entries(customDefaults)
              .filter(([k]) => !k.startsWith('picto'))
              .map(([, v]) => v);
            if (customTextValues.length > 0) {
              textLignes.forEach((l, i) => {
                defaultTexts[l.id] = i < customTextValues.length ? customTextValues[i] : (apiDefaults[l.id] || '');
              });
            } else {
              textLignes.forEach(l => {
                if (apiDefaults[l.id]) defaultTexts[l.id] = apiDefaults[l.id];
              });
            }
            lignes.filter(l => l.type === 'picto').forEach(l => {
              if (customDefaults[l.id]) defaultTexts[l.id] = customDefaults[l.id];
              else if (apiDefaults[l.id]) defaultTexts[l.id] = apiDefaults[l.id];
            });
            return {
              filename: p.filename,
              number: p.number,
              name: `Style ${p.number}`,
              preview: p.preview || '',
              lines: lignes,
              allLines: allLignes,
              nblignes,
              nbpictos,
              combinaisons: cfg?.combinaisons || [],
              defaultTexts: defaultTexts,
              declinaisons: cfg?.declinaisons || [],
              diviseur: cfg?.diviseur || 10
            };
          });
        }
      } catch (err) {
        console.error('Failed to load predesigns from API:', err);
      }
    }

    if (this.predesigns.length > 0) {
      this.renderDesigns(this.predesigns);

      if (this.pendingUrlParams) {
        // URL params present: skip default design, restore directly from URL
        await this.restoreFromUrlParams();
      } else {
        await this.selectDesign(this.predesigns[0]);
      }
    } else {
      console.warn('No predesigns found');
      this.elements.designsGrid.innerHTML = '<p style="text-align:center;color:#6d7385;">Aucun design disponible</p>';
    }
  }

  toggleSection(gridEl, count) {
    const section = gridEl ? gridEl.closest('.product-customizer__section') : null;
    if (section) section.style.display = count < 1 ? 'none' : '';
  }

  renderDesigns(predesigns) {
    if (!this.elements.designsGrid) return;
    this.toggleSection(this.elements.designsGrid, predesigns.length);

    this.elements.designsGrid.innerHTML = predesigns.map((design, idx) => {
      const langSuffix = this.getLocalizedThumbnailSuffix();
      const productPreview = `/assets/images/predesign/design_${this.config.productLegacyId}_${design.number}${langSuffix}.png`;
      // Build text preview from design lines (shows actual typo of this design)
      const textLines = (design.lines || []).filter(l => l.type === 'text');
      const defaults = design.defaultTexts || {};
      const textPreviewHtml = textLines.map((l, i) => {
        const text = defaults[l.id] || defaults[`phrase${i+1}`] || 'Texte';
        const fontMap = {
          'Brandon-Bold': { family: "'Inter', sans-serif", weight: '700', style: 'normal' },
          'VeneerCleanSoft': { family: "'Archivo', sans-serif", weight: '400', style: 'normal' },
          'Heritage': { family: "Georgia, 'Times New Roman', serif", weight: '400', style: 'italic' },
          'Magnolia': { family: "Georgia, 'Times New Roman', serif", weight: '400', style: 'italic' },
          'Treadstone-Regular': { family: "'Courier New', Courier, monospace", weight: '600', style: 'normal' },
          'Pockota-Medium': { family: "'Trebuchet MS', Verdana, sans-serif", weight: '500', style: 'normal' },
          'Skate-Medium': { family: "'Impact', 'Arial Black', sans-serif", weight: '500', style: 'normal' },
          'Roboto-Condensed-SB': { family: "'Arial Narrow', Arial, sans-serif", weight: '600', style: 'normal' },
          'Nexa': { family: "'Verdana', Geneva, sans-serif", weight: '400', style: 'normal' },
          'Yellow Design Studio': { family: "'Brush Script MT', cursive", weight: '500', style: 'normal' },
          'Raleway-Regular': { family: "'Inter', sans-serif", weight: '400', style: 'normal' },
        };
        const fm = fontMap[l.font] || { family: "'Inter', sans-serif", weight: '400', style: 'normal' };
        const font = fm.family;
        const weight = fm.weight;
        const fontStyle = fm.style !== 'normal' ? `font-style:${fm.style};` : '';
        const size = i === 0 ? '10px' : (i === 1 ? '14px' : '9px');
        return `<span style="font-family:${font};font-weight:${weight};${fontStyle}font-size:${size};line-height:1.2;${l.uppercase ? 'text-transform:uppercase;' : ''}">${text}</span>`;
      }).join('');

      return `
        <div class="product-customizer__design-item${idx === 0 ? ' product-customizer__design-item--active' : ''}"
             data-filename="${design.filename}"
             data-number="${design.number}">
          <img src="${this.config.apiEndpoint}${productPreview}"
               alt="Style ${design.number}"
               style="display:block;"
               onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
          <div style="display:none;flex-direction:column;align-items:center;justify-content:center;gap:2px;padding:12px 8px;text-align:center;min-height:80px;color:#1a1a2e;">
            ${textPreviewHtml}
          </div>
        </div>
      `;
    }).join('');

    this.elements.designsGrid.querySelectorAll('.product-customizer__design-item').forEach(item => {
      item.addEventListener('click', () => {
        const filename = item.dataset.filename;
        const design = this.predesigns.find(p => p.filename === filename);
        if (design) this.selectDesign(design);
      });
    });
  }

  async selectDesign(design) {
    // Save user inputs before switching (except on first load)
    if (!this.isFirstLoad) {
      this.saveUserInputs();
    }

    this.updateDesignSelection(design.filename);
    await this.loadPredesignData(design.filename);

    this.isFirstLoad = false;
  }

  updateDesignSelection(filename) {
    this.elements.designsGrid.querySelectorAll('.product-customizer__design-item').forEach(item => {
      item.classList.toggle('product-customizer__design-item--active', item.dataset.filename === filename);
    });
  }

  async loadPredesignData(filename) {
    // Find predesign data from local config instead of API
    const predesign = this.predesigns.find(p => p.filename === filename);

    if (predesign) {
      this.predesignData = predesign;
      this.currentPredesign = filename;

      // Fetch color thumbnails only ONCE on first load
      // Variant images NEVER change on style switch to avoid unnecessary API calls
      if (!this.thumbnailsLoaded) {
        await this.fetchThumbnailsAsync(filename);
        this.thumbnailsLoaded = true;
      }

      // Render word inputs from local predesign lines data
      this.renderWordInputs(predesign.lines, filename);
      await this.renderPictos();

      // Restore user inputs or apply defaults
      if (Object.keys(this.savedUserInputs).length > 0) {
        this.restoreUserInputs();
      } else {
        this.applyDefaultTexts(filename);
      }

      // Select default picto (first one)
      this.selectDefaultPicto();

      this.updateSummary();
      this.calculateDeliveryDate();

      // Resolve current declinaison based on selected size
      this.resolveCurrentDeclinaison();

      // Generate preview via API
      await this.generatePreview();
    }
  }

  async fetchThumbnailsAsync(filename) {
    try {
      const endpoint = this.config.apiEndpoint;

      // Fetch thumbnails and combinaisons in parallel
      const [thumbRes, comboRes] = await Promise.all([
        fetch(`${endpoint}/api/predesign/thumbnails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename,
            id_product: this.config.productLegacyId,
            id_product_attribute: this.config.variantLegacyId
          })
        }),
        fetch(`${endpoint}/api/predesign/load`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename,
            id_product: this.config.productLegacyId,
            id_product_attribute: this.config.variantLegacyId
          })
        })
      ]);

      const thumbData = await thumbRes.json();
      const comboData = await comboRes.json();

      if (thumbData.success && thumbData.thumbnails) {
        this.colorThumbnails = thumbData.thumbnails;
      }

      // Build static image fallback maps from combinaisons
      if (comboData.data?.combinaisons) {
        this.colorStaticImages = {};
        this.baseColorImages = {};
        comboData.data.combinaisons.forEach(c => {
          if (c.image?.filename) {
            const imgPath = `${endpoint}/assets/images/image_produit/${c.image.filename}`;
            this.colorStaticImages[c.nom] = imgPath;
            // Map base color (first part before " / ") for fallback
            const baseColor = c.nom.split(' / ')[0];
            if (!this.baseColorImages[baseColor]) {
              this.baseColorImages[baseColor] = imgPath;
            }
          }
        });
      }

      this.updateColorThumbnails();
    } catch (e) {
      // Thumbnails failed to load, keep using fallback images
    }
  }

  updateColorThumbnails() {
    if (!this.elements.colorsGrid) return;

    // Check if any variant images actually have non-null values
    const hasRealVariantImages = this.config.variantImages &&
      Object.values(this.config.variantImages).some(v => v !== null && v !== undefined);

    // Skip only if variant images with actual URLs exist
    if (hasRealVariantImages) return;

    // Update Liquid-rendered color items with API thumbnails or static fallbacks
    this.elements.colorsGrid.querySelectorAll('.product-customizer__color-item').forEach(item => {
      const colorName = item.dataset.color;

      // Priority: API thumbnail > exact color static image > base color static image
      const thumbnailUrl = this.colorThumbnails[colorName];
      let imgUrl = null;
      if (thumbnailUrl) {
        imgUrl = `${this.config.apiEndpoint}${thumbnailUrl}`;
      } else if (this.colorStaticImages?.[colorName]) {
        imgUrl = this.colorStaticImages[colorName];
      } else if (this.baseColorImages) {
        const baseColor = colorName.split(' / ')[0];
        if (this.baseColorImages[baseColor]) {
          imgUrl = this.baseColorImages[baseColor];
        }
      }

      if (!imgUrl) return;

      const img = item.querySelector('img');
      const placeholder = item.querySelector('.product-customizer__color-placeholder');
      if (img) {
        img.src = imgUrl;
      } else if (placeholder) {
        const newImg = document.createElement('img');
        newImg.src = imgUrl;
        newImg.alt = colorName;
        newImg.loading = 'lazy';
        placeholder.replaceWith(newImg);
      }
    });
  }

  applyDefaultTexts(filename) {
    // Custom defaults are stored with one style's IDs — map by position across styles
    const customDefaults = this.getLocalizedDefaultTexts();
    const predesign = this.predesigns.find(p => p.filename === filename);
    const apiDefaults = predesign?.defaultTexts || {};
    const customTextValues = Object.entries(customDefaults)
      .filter(([k]) => !k.startsWith('picto'))
      .map(([, v]) => v);

    const inputs = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input');
    if (customTextValues.length > 0) {
      inputs.forEach((input, index) => {
        input.value = index < customTextValues.length ? customTextValues[index] : (apiDefaults[input.name] || '');
      });
    } else {
      inputs.forEach(input => {
        if (apiDefaults[input.name]) input.value = apiDefaults[input.name];
      });
    }
    this.updateClearButton();
  }

  detectLanguage() {
    try {
      if (typeof Weglot !== 'undefined' && Weglot.getCurrentLang) return Weglot.getCurrentLang();
    } catch {}
    const htmlLang = document.documentElement.lang || '';
    if (htmlLang.startsWith('en')) return 'en';
    if (htmlLang.startsWith('de')) return 'de';
    if (htmlLang.startsWith('it')) return 'it';
    if (htmlLang.startsWith('es')) return 'es';
    return 'fr';
  }

  initWeglot() {
    const onWeglotReady = () => {
      // Re-detect language once Weglot is loaded
      const lang = this.detectLanguage();
      if (lang !== this.currentLang) {
        this.currentLang = lang;
        this.onLanguageChanged();
      }

      // Listen for future language changes (no page reload)
      try {
        Weglot.on('languageChanged', (newLang) => {
          this.currentLang = newLang;
          this.onLanguageChanged();
        });
      } catch {}
    };

    // Weglot may load after our script — poll for it
    if (typeof Weglot !== 'undefined' && Weglot.getCurrentLang) {
      onWeglotReady();
    } else {
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        if (typeof Weglot !== 'undefined' && Weglot.getCurrentLang) {
          clearInterval(checkInterval);
          onWeglotReady();
        }
        if (attempts > 30) clearInterval(checkInterval); // Stop after 15s
      }, 500);
    }
  }

  onLanguageChanged() {
    // Update default texts for new language
    this.applyDefaultTexts(this.currentPredesign);
    this.selectDefaultPicto();

    // Update design thumbnail images with language suffix
    if (this.elements.designsGrid) {
      const langSuffix = this.getLocalizedThumbnailSuffix();
      this.elements.designsGrid.querySelectorAll('img[src*="design_"]').forEach(img => {
        const src = img.getAttribute('src') || '';
        const newSrc = src.replace(/(design_\d+_\d+)(_[a-z]{2})?(\.png)/, `$1${langSuffix}$3`);
        if (newSrc !== src) img.src = newSrc;
      });
    }

    // Regenerate preview with new language texts
    this.generatePreview();
  }

  getLocalizedDefaultTexts() {
    const lang = this.currentLang || 'fr';
    // Priority: i18n texts for current language, fallback to FR flat texts
    const i18n = this.config?.customDefaultTextsI18n;
    if (i18n && typeof i18n === 'object') {
      const langTexts = i18n[lang];
      if (langTexts && Object.keys(langTexts).length > 0) return langTexts;
      // Fallback to FR
      if (i18n.fr && Object.keys(i18n.fr).length > 0) return i18n.fr;
    }
    return this.config?.customDefaultTexts || {};
  }

  getLocalizedThumbnailSuffix() {
    const lang = this.currentLang || 'fr';
    return lang === 'fr' ? '' : `_${lang}`;
  }

  selectDefaultPicto() {
    // Skip if user already chose or cleared a picto
    if (this.userHasInteractedWithPicto) return;

    // Use custom default picto from metafield if available
    const customPicto = this.getLocalizedDefaultTexts()?.picto1;
    if (customPicto && !this.currentPicto && this.elements.pictosGrid) {
      // Match by code, name, or case-insensitive name (metafield stores "bird2", grid may use letter codes or names)
      const lowerPicto = customPicto.toLowerCase();
      const allPictos = this.elements.pictosGrid.querySelectorAll('.product-customizer__picto-item');
      let matchingPicto = null;
      for (const el of allPictos) {
        const code = (el.getAttribute('data-code') || '').toLowerCase();
        const name = (el.getAttribute('data-name') || '').toLowerCase();
        if (code === lowerPicto || name === lowerPicto) { matchingPicto = el; break; }
      }
      if (matchingPicto) { this.selectPicto(matchingPicto); return; }
    }
    const firstPicto = this.elements.pictosGrid.querySelector('.product-customizer__picto-item');
    if (firstPicto && !this.currentPicto) {
      this.selectPicto(firstPicto);
    }
  }

  renderColors(colors) {
    if (!colors || !this.elements.colorsGrid) return;
    this.toggleSection(this.elements.colorsGrid, colors.length);

    this.elements.colorsGrid.innerHTML = colors.map((color, idx) => {
      // Priority: API thumbnail (with text) > static product image
      const thumbnailUrl = this.colorThumbnails[color.nom];
      let imageUrl;
      if (thumbnailUrl) {
        imageUrl = `${this.config.apiEndpoint}${thumbnailUrl}`;
      } else {
        imageUrl = `${this.config.apiEndpoint}/assets/images/image_produit/${color.image.filename}`;
      }

      return `
        <div class="product-customizer__color-item${idx === 0 ? ' product-customizer__color-item--active' : ''}"
             data-color="${color.nom}"
             data-image="${color.image.filename}"
             data-text-color="${color.couleurtypoweb || ''}">
          <img src="${imageUrl}"
               alt="${color.nom}"
               onerror="this.src='${this.config.apiEndpoint}/assets/images/image_produit/${color.image.filename}'">
        </div>
      `;
    }).join('');

    this.elements.colorsGrid.querySelectorAll('.product-customizer__color-item').forEach(item => {
      item.addEventListener('click', () => this.selectColor(item));
    });

    if (colors.length > 0) {
      this.currentColor = colors[0].nom;
      this.updateColorSummary(this.currentColor);
      // Update color indicator for first selected color
      const firstColorItem = this.elements.colorsGrid.querySelector('.product-customizer__color-item--active');
      if (firstColorItem) {
        this.updateCurrentColorIndicator(firstColorItem);
      }
      this.updateVariantFromOptions();
    }
  }

  selectColor(item, dispatchEvent = true) {
    this.elements.colorsGrid.querySelectorAll('.product-customizer__color-item').forEach(el => {
      el.classList.remove('product-customizer__color-item--active');
    });
    item.classList.add('product-customizer__color-item--active');
    this.currentColor = item.dataset.color;
    this.updateColorSummary(this.currentColor);
    this.updateCurrentColorIndicator(item);
    this.updateVariantFromOptions();
    this.updateAddToCartFromStock(this.currentColor);
    this.generatePreview();

    if (dispatchEvent) {
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variantId: item.dataset.variantId, color: this.currentColor, source: 'customizer' }
      }));
    }
  }

  updateCurrentColorIndicator(item) {
    if (!this.elements.currentColorIndicator) return;

    const colorName = item.dataset.color;
    const nameEl = this.elements.currentColorIndicator.querySelector('.product-customizer__current-color-name');

    if (nameEl) {
      nameEl.textContent = colorName || '-';
    }
  }

  updateColorSummary(colorName) {
    const parts = colorName.split(' / ');
    if (this.elements.summaryColor) {
      this.elements.summaryColor.textContent = parts[0] || '-';
    }
    if (this.elements.summaryTypo) {
      this.elements.summaryTypo.textContent = parts[1] || '-';
    }
  }

  findVariantByOptions(size, color) {
    if (!this.config || !this.config.variants) return null;
    return this.config.variants.find(v => v.size === size && v.color === color);
  }

  updateVariantFromOptions() {
    if (!this.currentColor) return;
    const size = this.currentSize || this.config.defaultSize;
    const variant = size
      ? this.findVariantByOptions(size, this.currentColor)
      : this.config.variants?.find(v => v.color === this.currentColor);
    if (variant) {
      this.config.variantId = variant.id;
      this.updatePrice();
      this.updateVariantMedias(variant);
    }
  }

  updateVariantMedias(variant) {
    // Update URL with variant ID
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Sync with main-product section: click the matching size/color buttons
    const mainProductSection = document.querySelector('.main-product-section');
    if (mainProductSection) {
      // Sync size button
      if (this.currentSize) {
        const sizeBtn = mainProductSection.querySelector(`.mp-size-variants__item[data-size="${this.currentSize}"]`);
        if (sizeBtn && !sizeBtn.classList.contains('mp-size-variants__item--active')) {
          sizeBtn.click();
          return; // The click handler in main-product.js will handle the rest
        }
      }
      // Sync color button
      if (this.currentColor) {
        const colorBtn = mainProductSection.querySelector(`.mp-color-variants__item[data-color="${this.currentColor}"]`);
        if (colorBtn && !colorBtn.classList.contains('mp-color-variants__item--active')) {
          colorBtn.click();
          return;
        }
      }
      // If buttons don't exist (hidden), call updateProductImages directly
      if (variant.medias && variant.medias.length > 0 && mainProductSection.updateProductImages) {
        mainProductSection.updateProductImages(JSON.stringify(variant.medias));
      }
    }
  }

  renderWordInputs(lignes, filename) {
    if (!lignes || !this.elements.wordsContainer) return;

    const textLines = lignes.filter(l => l.type === 'text');
    this.toggleSection(this.elements.wordsContainer, textLines.length);
    let position = 1;

    this.elements.wordsContainer.innerHTML = textLines.map(line => {
      const html = `
        <div class="product-customizer__word-row" data-line-id="${line.id}">
          <div class="product-customizer__word-number h2">${position}</div>
          <input type="text"
                 class="product-customizer__word-input"
                 id="input-${line.id}"
                 name="${line.id}"
                 maxlength="${line.maxLength || line.maxChars || 30}"
                 placeholder="Entrez votre texte...">
          <div class="product-customizer__word-drag" title="Glisser pour réorganiser">
            <span></span><span></span><span></span>
          </div>
        </div>
      `;
      position++;
      return html;
    }).join('');

    this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input').forEach(input => {
      input.addEventListener('input', () => {
        // Validate against banned words
        const validation = this.validateText(input.value);
        if (!validation.valid) {
          this.showInputError(input, `"${validation.word}" n'est pas autorise`);
          return; // Don't generate preview
        }
        this.clearInputError(input);
        this.updateClearButton();
        this.debouncedGeneratePreview();
      });
      input.addEventListener('blur', () => {
        const validation = this.validateText(input.value);
        if (!validation.valid) {
          this.showInputError(input, `"${validation.word}" n'est pas autorise`);
          return;
        }
        this.clearInputError(input);
        clearTimeout(this.debounceTimer);
        this.generatePreview();
      });
    });

    // Setup drag and drop
    this.setupDragAndDrop();
    this.updateClearButton();
    this.restoreWordsOrder();
  }

  setupDragAndDrop() {
    const container = this.elements.wordsContainer;
    if (!container) return;

    this.dragState = {
      isDragging: false,
      element: null,
      placeholder: null,
      startY: 0,
      startIndex: 0,
      offsetY: 0
    };

    container.querySelectorAll('.product-customizer__word-row').forEach(row => {
      this.setupRowDragEvents(row);
    });
  }

  setupRowDragEvents(row) {
    const dragHandle = row.querySelector('.product-customizer__word-drag');
    if (!dragHandle) return;

    const onPointerDown = (e) => {
      if (e.target.closest('.product-customizer__word-input')) return;

      e.preventDefault();
      dragHandle.setPointerCapture(e.pointerId);

      const rect = row.getBoundingClientRect();
      this.dragState = {
        isDragging: true,
        element: row,
        placeholder: this.createPlaceholder(row),
        startY: e.clientY,
        startIndex: this.getRowIndex(row),
        offsetY: e.clientY - rect.top
      };

      row.classList.add('product-customizer__word-row--dragging');
      row.style.position = 'fixed';
      row.style.width = `${rect.width}px`;
      row.style.left = `${rect.left}px`;
      row.style.top = `${e.clientY - this.dragState.offsetY}px`;
      row.style.zIndex = '1000';

      row.parentNode.insertBefore(this.dragState.placeholder, row);
    };

    const onPointerMove = (e) => {
      if (!this.dragState.isDragging || this.dragState.element !== row) return;

      e.preventDefault();
      row.style.top = `${e.clientY - this.dragState.offsetY}px`;

      const siblings = Array.from(this.elements.wordsContainer.querySelectorAll('.product-customizer__word-row:not(.product-customizer__word-row--dragging)'));

      for (const sibling of siblings) {
        const rect = sibling.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;

        if (e.clientY < midY) {
          if (sibling !== this.dragState.placeholder.nextSibling) {
            sibling.parentNode.insertBefore(this.dragState.placeholder, sibling);
          }
          return;
        }
      }

      const container = this.elements.wordsContainer;
      if (this.dragState.placeholder.nextSibling !== null) {
        container.appendChild(this.dragState.placeholder);
      }
    };

    const onPointerUp = (e) => {
      if (!this.dragState.isDragging || this.dragState.element !== row) return;

      dragHandle.releasePointerCapture(e.pointerId);

      row.classList.remove('product-customizer__word-row--dragging');
      row.style.position = '';
      row.style.width = '';
      row.style.left = '';
      row.style.top = '';
      row.style.zIndex = '';

      if (this.dragState.placeholder && this.dragState.placeholder.parentNode) {
        this.dragState.placeholder.parentNode.insertBefore(row, this.dragState.placeholder);
        this.dragState.placeholder.remove();
      }

      const newIndex = this.getRowIndex(row);
      if (newIndex !== this.dragState.startIndex) {
        this.updateWordNumbers();
        this.saveWordsOrder();
        this.generatePreview();
      }

      this.dragState = { isDragging: false, element: null, placeholder: null, startY: 0, startIndex: 0, offsetY: 0 };
    };

    dragHandle.addEventListener('pointerdown', onPointerDown);
    dragHandle.addEventListener('pointermove', onPointerMove);
    dragHandle.addEventListener('pointerup', onPointerUp);
    dragHandle.addEventListener('pointercancel', onPointerUp);

    dragHandle.style.touchAction = 'none';
  }

  createPlaceholder(row) {
    const placeholder = document.createElement('div');
    placeholder.className = 'product-customizer__word-row product-customizer__word-row--placeholder';
    placeholder.style.height = `${row.offsetHeight}px`;
    placeholder.style.background = 'var(--primary-10, #e8e9ec)';
    placeholder.style.border = '2px dashed var(--primary-30, #bfc4d4)';
    placeholder.style.visibility = 'visible';
    return placeholder;
  }

  getRowIndex(row) {
    const rows = Array.from(this.elements.wordsContainer.querySelectorAll('.product-customizer__word-row:not(.product-customizer__word-row--placeholder)'));
    return rows.indexOf(row);
  }

  updateWordNumbers() {
    const rows = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-row');
    rows.forEach((row, idx) => {
      const numberEl = row.querySelector('.product-customizer__word-number');
      if (numberEl) {
        numberEl.textContent = idx + 1;
      }
    });
  }

  async renderPictos() {
    if (!this.elements.pictosGrid) return;

    // Check if current design has picto lines
    const hasPictoLine = this.predesignData?.lines?.some(l => l.type === 'picto');
    if (!hasPictoLine) {
      this.toggleSection(this.elements.pictosGrid, 0);
      // Also hide the "Sans picto" button
      if (this.elements.btnNoPicto) {
        const noPictoSection = this.elements.btnNoPicto.closest('.product-customizer__section');
        if (noPictoSection) noPictoSection.style.display = 'none';
      }
      return;
    }

    // Use cached pictos if available (they don't change between design switches)
    if (!this._cachedPictos) {
      try {
        const res = await fetch(`${this.config.apiEndpoint}/api/pictos`);
        const data = await res.json();
        this._cachedPictos = data.pictos || [];
      } catch (err) {
        console.error('Failed to fetch pictos:', err);
        this._cachedPictos = [];
      }
    }

    const pictos = this._cachedPictos;
    const apiBase = this.config.apiEndpoint;
    this.toggleSection(this.elements.pictosGrid, pictos.length);

    this.elements.pictosGrid.innerHTML = pictos.map((picto) => `
      <div class="product-customizer__picto-item"
           data-code="${picto.code}"
           data-name="${picto.name}">
        <img src="${apiBase}${picto.url}" alt="${picto.name}" onerror="this.parentElement.style.display='none'">
      </div>
    `).join('');

    this.elements.pictosGrid.querySelectorAll('.product-customizer__picto-item').forEach(item => {
      item.addEventListener('click', () => this.selectPicto(item));
    });

    // Preserve user-selected picto across style switches
    if (this.userHasInteractedWithPicto) {
      if (this.currentPicto) {
        const savedCode = this.currentPicto;
        const matchingItem = this.elements.pictosGrid.querySelector(
          `.product-customizer__picto-item[data-code="${savedCode}"]`
        );
        if (matchingItem) {
          matchingItem.classList.add('product-customizer__picto-item--active');
          if (this.elements.btnNoPicto) {
            this.elements.btnNoPicto.classList.remove('product-customizer__clear-btn--active');
          }
        } else {
          this.currentPicto = null;
        }
      } else {
        // User chose "Sans picto" — keep it cleared
        if (this.elements.btnNoPicto) {
          this.elements.btnNoPicto.classList.add('product-customizer__clear-btn--active');
        }
      }
    }
  }

  selectPicto(item) {
    this.elements.pictosGrid.querySelectorAll('.product-customizer__picto-item').forEach(el => {
      el.classList.remove('product-customizer__picto-item--active');
    });
    item.classList.add('product-customizer__picto-item--active');
    this.currentPicto = item.dataset.code;
    this.userHasInteractedWithPicto = true;
    if (this.elements.btnNoPicto) {
      this.elements.btnNoPicto.classList.remove('product-customizer__clear-btn--active');
    }
    this.generatePreview();
  }

  clearPicto() {
    this.elements.pictosGrid.querySelectorAll('.product-customizer__picto-item').forEach(el => {
      el.classList.remove('product-customizer__picto-item--active');
    });
    this.currentPicto = null;
    this.userHasInteractedWithPicto = true;
    if (this.elements.btnNoPicto) {
      this.elements.btnNoPicto.classList.add('product-customizer__clear-btn--active');
    }
    this.generatePreview();
  }

  clearWords() {
    this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input').forEach(input => {
      input.value = '';
    });
    this.savedUserInputs = {};
    this.updateClearButton();
    this.generatePreview();
  }

  updateClearButton() {
    const inputs = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input');
    const hasText = Array.from(inputs).some(input => input.value.trim() !== '');
    if (this.elements.btnClearWords) {
      this.elements.btnClearWords.disabled = !hasText;
    }
  }

  debouncedGeneratePreview() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.generatePreview(), 2000);
  }

  async generatePreview() {
    // Suppressed while restoring from cart/url params. The restore flow
    // triggers one explicit generatePreview once all values are applied.
    if (this._restoringFromParams) return;
    if (!this.currentPredesign || !this.currentColor) {
      this.hideLoading();
      return;
    }

    // Sequence id: if a newer call started while we were waiting for the
    // network, discard this (stale) response instead of overwriting the image.
    const genId = (this._previewGenId = (this._previewGenId || 0) + 1);

    this.showLoading();

    const formData = {
      predesign: this.currentPredesign,
      id_product: this.config.productLegacyId,
      id_product_attribute: this.config.variantLegacyId,
      couleur: this.currentColor
    };

    // Get text values in DOM order (respects drag reorder),
    // then map them to XML-order keys so the backend renders in the visual order.
    let hasBannedWord = false;
    const orderedRows = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-row');
    const domValues = [];
    orderedRows.forEach((row) => {
      const input = row.querySelector('.product-customizer__word-input');
      const value = input ? input.value.trim() : '';
      if (value) {
        const validation = this.validateText(value);
        if (!validation.valid) {
          this.showInputError(input, `"${validation.word}" n'est pas autorise`);
          hasBannedWord = true;
          return;
        }
        this.clearInputError(input);
      }
      domValues.push(value);
    });

    // XML text line IDs in their original position order
    const xmlTextIds = (this.predesignData?.lines || [])
      .filter(l => l.type === 'text')
      .map(l => l.id);

    // Assign DOM-order values to XML-order keys
    xmlTextIds.forEach((id, i) => {
      if (domValues[i]) formData[id] = domValues[i];
    });

    if (hasBannedWord) {
      this.hideLoading();
      return;
    }

    if (this.currentPicto) {
      formData.picto1 = this.currentPicto;
    }

    // Send size-specific declinaison ref (handles both diviseur and per-size lignes)
    if (this.currentDeclinaison?.ref || this.currentDeclinaison?.ref_declinaison) {
      formData.declinaison_ref = this.currentDeclinaison.ref || this.currentDeclinaison.ref_declinaison;
    } else if (this.currentDeclinaison?.diviseur) {
      formData.diviseur_override = this.currentDeclinaison.diviseur;
    }

    try {
      const response = await fetch(`${this.config.apiEndpoint}/api/generate/web`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (genId !== this._previewGenId) return;
      if (data.url) {
        const imageUrl = `${this.config.apiEndpoint}${data.url}`;
        this.showPreview(imageUrl);
        // Store versionFile from generate response for order immutability
        if (data.xml) {
          try {
            const xmlData = typeof data.xml === 'string' ? JSON.parse(data.xml) : data.xml;
            if (xmlData.versionFile) this.currentVersionFile = xmlData.versionFile;
          } catch {}
        }
        if (this.elements.btnAddToCart && !this._isOutOfStock) {
          this.elements.btnAddToCart.disabled = false;
        }
      } else {
        this.hideLoading();
      }
    } catch (e) {
      if (genId !== this._previewGenId) return;
      console.error('Generate preview error:', e);
      this.hideLoading();
    }
  }

  _buildStockMap() {
    const stockByColor = {};
    for (const entry of (this._stockData || [])) {
      const key = this.normalizeColorName(entry.colorName);
      if (!key) continue;
      // Filter by selected size for multi-size products
      if (this.currentSize && entry.size) {
        if (this.normalizeColorName(entry.size) !== this.normalizeColorName(this.currentSize)) continue;
      }
      stockByColor[key] = (stockByColor[key] || 0) + (entry.quantity || 0);
    }
    this._stockByColor = stockByColor;
    this._hasStockData = Object.keys(stockByColor).length > 0;
  }

  refilterStock() {
    if (!this._stockData) return;
    this._buildStockMap();
    const items = this.elements.colorsGrid?.querySelectorAll('.product-customizer__color-item');
    if (!items) return;
    items.forEach((item) => {
      const isOos = this.isColorOutOfStock(item.dataset.color);
      item.classList.toggle('product-customizer__color-item--oos', isOos);
      item.style.opacity = isOos ? '0.35' : '';
      item.style.pointerEvents = isOos ? 'none' : '';
    });
    const activeItem = this.elements.colorsGrid.querySelector('.product-customizer__color-item--active');
    if (activeItem && this.isColorOutOfStock(activeItem.dataset.color)) {
      const firstAvailable = Array.from(items).find(el => !this.isColorOutOfStock(el.dataset.color));
      if (firstAvailable) this.selectColor(firstAvailable);
      else this.setOutOfStock(true);
    } else {
      this.updateAddToCartFromStock(activeItem?.dataset.color);
    }
  }

  normalizeColorName(name) {
    return (name || '').toString().toLowerCase().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
  }

  isColorOutOfStock(colorAttr) {
    if (!this._hasStockData) return false;
    const bgColor = (colorAttr || '').split('/')[0];
    const key = this.normalizeColorName(bgColor);
    const qty = this._stockByColor?.[key];
    return qty === undefined || qty <= 0;
  }

  updateAddToCartFromStock(colorAttr) {
    if (!this._hasStockData) return;
    const isOos = this.isColorOutOfStock(colorAttr);
    this.setOutOfStock(isOos);
  }

  setOutOfStock(outOfStock) {
    this._isOutOfStock = outOfStock;
    if (this.elements.btnAddToCart) {
      this.elements.btnAddToCart.disabled = outOfStock;
      if (outOfStock) {
        this._originalBtnText = this._originalBtnText || this.elements.btnAddToCart.textContent;
        this.elements.btnAddToCart.textContent = 'Rupture de stock';
      } else if (this._originalBtnText) {
        this.elements.btnAddToCart.textContent = this._originalBtnText;
      }
    }
  }

  showLoading() {
    if (this.elements.previewPlaceholder) this.elements.previewPlaceholder.style.display = 'none';
    if (this.elements.previewImage) {
      this.elements.previewImage.classList.add('product-customizer__preview-image--loading');
    }
    if (this.elements.previewLoading) {
      this.elements.previewLoading.style.display = 'flex';
    }
  }

  hideLoading() {
    if (this.elements.previewImage) {
      this.elements.previewImage.classList.remove('product-customizer__preview-image--loading');
    }
    if (this.elements.previewLoading) {
      this.elements.previewLoading.style.display = 'none';
    }
    if (this.elements.previewImage && !this.elements.previewImage.src) {
      if (this.elements.previewPlaceholder) this.elements.previewPlaceholder.style.display = 'flex';
    }
  }

  showPreview(url) {
    const img = new Image();
    img.onload = () => {
      if (this.elements.previewImage) {
        this.elements.previewImage.src = url;
        this.elements.previewImage.style.display = 'block';
      }
      if (this.elements.previewPlaceholder) this.elements.previewPlaceholder.style.display = 'none';
      this.hideLoading();
    };
    img.onerror = () => {
      this.hideLoading();
    };
    img.src = url;
  }

  handleQuantityChange(e) {
    const action = e.currentTarget.dataset.action;
    if (action === 'increase') {
      this.quantity++;
    } else if (action === 'decrease' && this.quantity > 1) {
      this.quantity--;
    }
    this.syncQuantityDisplay();
    this.updatePrice();
  }

  handleQuantitySelect(e) {
    this.quantity = parseInt(e.target.value, 10);
    this.syncQuantityDisplay();
    this.updatePrice();
  }

  syncQuantityDisplay() {
    if (this.elements.quantityValue) {
      this.elements.quantityValue.textContent = this.quantity;
    }
    if (this.elements.quantitySelect) {
      this.elements.quantitySelect.value = this.quantity;
    }
  }

  openZoomModal() {
    if (!this.elements.previewImage || !this.elements.previewImage.src) return;

    let modal = document.querySelector('.product-customizer__zoom-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'product-customizer__zoom-modal';
      modal.innerHTML = `
        <div class="product-customizer__zoom-modal-content">
          <button type="button" class="product-customizer__zoom-modal-close" aria-label="Fermer">&times;</button>
          <img src="" alt="Preview agrandie">
        </div>
      `;
      document.body.appendChild(modal);

      modal.addEventListener('click', () => {
        this.closeZoomModal();
      });
    }

    const img = modal.querySelector('img');
    img.src = this.elements.previewImage.src;
    modal.classList.add('is-open');
    document.body.classList.add('no-scroll');
  }

  closeZoomModal() {
    const modal = document.querySelector('.product-customizer__zoom-modal');
    if (modal) {
      modal.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    }
  }

  handleSizeChange(e) {
    this.container.querySelectorAll('.product-customizer__size-btn').forEach(btn => {
      btn.classList.remove('product-customizer__size-btn--active');
    });
    e.currentTarget.classList.add('product-customizer__size-btn--active');

    this.currentSize = e.currentTarget.dataset.size;
    if (this.elements.summarySize) {
      this.elements.summarySize.textContent = e.currentTarget.textContent.trim();
    }
    this.updateVariantFromOptions();

    // Re-filter stock for the new size
    this.refilterStock();

    // Find matching declinaison for this size and regenerate preview
    this.resolveCurrentDeclinaison();
    clearTimeout(this.debounceTimer);
    this.generatePreview();
  }

  resolveCurrentDeclinaison() {
    if (!this.currentSize || !this.predesignData?.declinaisons?.length) {
      this.currentDeclinaison = null;
      return;
    }
    const size = this.currentSize.toLowerCase();
    const decls = this.predesignData.declinaisons;
    // 1. Exact match
    this.currentDeclinaison = decls.find(d => (d.ref || d.ref_declinaison || '').toLowerCase() === size)
      // 2. Ref ends with size (e.g. "PON-S" ends with "s")
      || decls.find(d => (d.ref || d.ref_declinaison || '').toLowerCase().endsWith('-' + size))
      // 3. Ref contains size as a word boundary match
      || decls.find(d => {
        const ref = (d.ref || d.ref_declinaison || '').toLowerCase();
        return ref.includes(size) && (ref.indexOf(size) === 0 || ref[ref.indexOf(size) - 1] === '-');
      })
      // 4. Fallback
      || decls.find(d => d.default) || decls[0] || null;
  }

  updatePrice() {
    const activeBtn = this.container.querySelector('.product-customizer__size-btn--active');
    if (activeBtn && activeBtn.dataset.price && this.elements.totalPrice) {
      const price = parseFloat(activeBtn.dataset.price.replace(',', '.').replace('€', ''));
      const total = price * this.quantity;
      this.elements.totalPrice.textContent = total.toFixed(0) + '€';
    }
  }

  updateSummary() {
    if (this.currentColor) {
      this.updateColorSummary(this.currentColor);
    }
  }

  async calculateDeliveryDate() {
    const productId = this.config?.productId || this.config?.id_product;
    if (!productId) {
      this._setDeliveryDateFallback();
      return;
    }

    try {
      const response = await fetch(`${this.config.apiEndpoint}/api/delivery/estimate/${productId}`);
      if (!response.ok) throw new Error('API error');

      const data = await response.json();
      if (data.success && (data.delivery_date_long || data.delivery_date)) {
        // Build long format from ISO date if API doesn't provide it
        if (!data.delivery_date_long && data.delivery_date) {
          const d = new Date(data.delivery_date);
          data.delivery_date_long = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        }
        if (!data.delivery_date_late_long && data.delivery_date_late) {
          const d = new Date(data.delivery_date_late);
          data.delivery_date_late_long = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        }

        this._updateDeliveryDisplay(data);

        if (data.atelier) {
          this.currentAtelier = data.atelier;
        }

        // Start countdown timer
        this._startDeliveryCountdown(data);
      } else {
        this._setDeliveryDateFallback();
      }
    } catch (error) {
      console.warn('Delivery date API unavailable, using fallback');
      this._setDeliveryDateFallback();
    }
  }

  _updateDeliveryDisplay(data) {
    const deliveryBlock = this.container?.querySelector('[data-delivery-customizer]');
    if (!deliveryBlock) return;

    // Primary delivery date
    const dateEl = deliveryBlock.querySelector('[data-delivery-date]');
    if (dateEl) dateEl.textContent = data.delivery_date_long;

    // Late delivery date (only show if different from primary)
    const lateEl = deliveryBlock.querySelector('[data-delivery-date-late]');
    const lateLine = deliveryBlock.querySelector('.product-customizer__delivery-late');
    if (lateEl && data.delivery_date_late_long && data.delivery_date_late_long !== data.delivery_date_long) {
      lateEl.textContent = data.delivery_date_late_long;
      if (lateLine) lateLine.style.display = '';
    } else if (lateLine) {
      lateLine.style.display = 'none';
    }

    // Update summary element
    if (this.elements.deliveryDate) {
      this.elements.deliveryDate.innerHTML = `<strong>${data.delivery_date_long}</strong>`;
    }
  }

  _startDeliveryCountdown(data) {
    if (this._deliveryInterval) clearInterval(this._deliveryInterval);

    const cutoffDeadline = data.cutoff_deadline ? new Date(data.cutoff_deadline) : null;
    const cutoffHour = data.cutoff_hour || 16;

    const tick = () => {
      const now = new Date();
      let deadline;

      if (cutoffDeadline) {
        deadline = new Date(cutoffDeadline);
        // If deadline has passed, shift to next day's cutoff
        if (now >= deadline) {
          deadline.setDate(deadline.getDate() + 1);
        }
      } else {
        deadline = new Date(now);
        deadline.setHours(cutoffHour, 0, 0, 0);
        if (now >= deadline) deadline.setDate(deadline.getDate() + 1);
      }

      const diffMs = Math.max(0, deadline - now);
      const totalMin = Math.floor(diffMs / 60000);
      const days = Math.floor(totalMin / 1440);
      const hours = Math.floor((totalMin % 1440) / 60);
      const mins = totalMin % 60;

      let countdownText = 'dans les ';
      if (days > 0) countdownText += `${days}j et ${hours}h`;
      else if (hours > 0) countdownText += `${hours}h`;
      else countdownText += `${mins}min`;

      const countdownEl = this.container?.querySelector('[data-countdown]');
      if (countdownEl) countdownEl.textContent = countdownText;
    };

    tick();
    this._deliveryInterval = setInterval(tick, 60000);
  }

  _setDeliveryDateFallback() {
    const date = new Date();
    let businessDays = 7;
    while (businessDays > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) businessDays--;
    }
    const formatted = date.toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long'
    });

    const dateEl = this.container?.querySelector('[data-delivery-date]');
    if (dateEl) dateEl.textContent = formatted;

    if (this.elements.deliveryDate) {
      this.elements.deliveryDate.innerHTML = `<strong>${formatted}</strong>`;
    }

    // Hide late line in fallback mode
    const lateLine = this.container?.querySelector('.product-customizer__delivery-late');
    if (lateLine) lateLine.style.display = 'none';
  }

  async addToCart() {
    if (!this.elements.previewImage || !this.elements.previewImage.src) return;

    const customData = this.getCustomizationData();

    try {
      if (this.editKey) {
        const removeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.editKey, quantity: 0 })
        });
        if (!removeRes.ok) {
          console.error('[Customizer] Failed to remove old cart item for edit');
        }
        this.editKey = null;
        const url = new URL(window.location.href);
        url.searchParams.delete('edit_key');
        window.history.replaceState({}, '', url.toString());
      }

      // 1) Add to cart first (fast, user sees immediate feedback)
      const properties = {
        '_customization_preview': this.elements.previewImage.src,
        '_customization_predesign': customData.predesign || '',
        '_customization_color': customData.color || '',
        '_customization_picto': customData.picto || '',
        ...Object.fromEntries(
          Object.entries(customData.texts || {}).filter(([k]) => k.startsWith('phrase')).map(([k, v]) => [`ligne ${k.replace('phrase', '')}`, v || ''])
        ),
        '_customization_product_id': String(customData.productId),
        '_customization_variant_id': String(customData.variantId),
        '_customization_config': JSON.stringify(customData)
      };

      const formData = {
        items: [{
          id: parseInt(this.config.variantId),
          quantity: this.quantity,
          properties
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        console.error('Add to cart failed');
        return;
      }

      // 2) Open drawer cart immediately
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.querySelector('side-cart')?.open?.();

      // 3) Save customization to configurateur API (non-blocking background)
      this._saveCustomizationAsync(customData);
      this.trackCartAddition(customData);
    } catch (e) {
      console.error('Add to cart error:', e);
    }
  }

  _saveCustomizationAsync(customData) {
    fetch(`${this.config.apiEndpoint}/api/customization/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_product: customData.productId,
        id_product_attribute: customData.variantId,
        mdmini: this.elements.previewImage.src,
        mdxml: JSON.stringify(customData)
      })
    }).then(res => res.json()).then(data => {
      if (data.success && data.customization_id) {
        // Update cart item with customization_id via cart/update
        this._updateCartItemCustomizationId(String(data.customization_id));
      }
    }).catch(err => {
      console.error('Customization save failed:', err);
    });
  }

  _updateCartItemCustomizationId(customizationId) {
    // Find the last added cart item and update its properties with the customization_id
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const item = cart.items.find(i => (i.properties?._customization_product_id) && !i.properties._customization_id);
        if (item) {
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: { ...item.properties, _customization_id: customizationId }
            })
          }).then(() => {
            document.dispatchEvent(new CustomEvent('cart:updated'));
          });
        }
      }).catch(() => {});
  }

  trackCartAddition(customData) {
    try {
      fetch(`${this.config.apiEndpoint}/api/tracking/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: this.config.shopDomain || window.Shopify?.shop || 'unknown',
          productTitle: this.config.productTitle || null,
          productId: String(customData.productId || ''),
          variantTitle: this.config.variantTitle || null,
          variantId: String(customData.variantId || ''),
          designStyle: customData.predesign || null,
          designColor: customData.color || null,
          designTexts: customData.texts || null,
          imageUrl: customData.previewUrl || null,
        })
      }).catch(() => {});
    } catch (e) {}
  }

  initGuaranteeSlider() {
    const guarantee = this.container.querySelector('[data-pc-guarantee]');
    if (!guarantee) return;

    const dataEl = guarantee.querySelector('.pc-guarantee__data');
    if (!dataEl) return;

    let items = [];
    try {
      items = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!items.length) return;

    const textEl = guarantee.querySelector('.pc-guarantee__text');
    const pagination = guarantee.querySelector('[data-pc-guarantee-pagination]');
    const delay = parseInt(guarantee.dataset.autoplayDelay, 10) || 3000;

    let currentIndex = 0;
    let timer = null;

    const updateSlide = (index) => {
      if (textEl) textEl.textContent = items[index].text;

      pagination.querySelectorAll('.pc-guarantee__pagination-item').forEach((item, i) => {
        const fill = item.querySelector('.pc-guarantee__pagination-fill');
        item.classList.toggle('pc-guarantee__pagination-item--active', i === index);
        if (fill) {
          fill.classList.remove('animating');
          if (i === index) {
            void fill.offsetWidth;
            fill.classList.add('animating');
          }
        }
      });
    };

    const nextSlide = () => {
      currentIndex = (currentIndex + 1) % items.length;
      updateSlide(currentIndex);
    };

    const startAutoplay = () => {
      stopAutoplay();
      timer = setInterval(nextSlide, delay);
    };

    const stopAutoplay = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    pagination.querySelectorAll('.pc-guarantee__pagination-item').forEach((item) => {
      item.addEventListener('click', () => {
        const index = parseInt(item.dataset.index, 10);
        if (!isNaN(index) && index !== currentIndex) {
          currentIndex = index;
          updateSlide(currentIndex);
          startAutoplay();
        }
      });
    });

    updateSlide(0);
    startAutoplay();

    this._guaranteeTimer = timer;
    this._stopGuarantee = stopAutoplay;
  }

  getCustomizationData() {
    const data = {
      predesign: this.currentPredesign,
      versionFile: this.currentVersionFile || null,
      color: this.currentColor,
      picto: this.currentPicto,
      texts: {},
      productId: this.config.productLegacyId,
      variantId: this.config.variantLegacyId,
      shopifyVariantId: this.config.variantId,
      previewUrl: this.elements.previewImage?.src || '',
      size: this.currentSize || null,
      declinaisonRef: this.currentDeclinaison?.ref || this.currentDeclinaison?.ref_declinaison || null
    };

    // Map DOM-order values to XML-order keys (same logic as generatePreview)
    const orderedRows = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-row');
    const domValues = [];
    orderedRows.forEach((row) => {
      const input = row.querySelector('.product-customizer__word-input');
      domValues.push(input ? input.value || '' : '');
    });
    const xmlTextIds = (this.predesignData?.lines || [])
      .filter(l => l.type === 'text')
      .map(l => l.id);
    xmlTextIds.forEach((id, i) => {
      data.texts[id] = domValues[i] || '';
    });

    return data;
  }

  shareCreation() {
    this.openShareModal();
  }

  openShareModal() {
    if (!this.elements.shareModal) return;

    this.elements.shareModalForm.style.display = '';
    this.elements.shareModalSuccess.style.display = 'none';

    if (this.elements.shareEmailTo) this.elements.shareEmailTo.value = '';
    if (this.elements.shareEmailFrom) this.elements.shareEmailFrom.value = '';
    if (this.elements.shareCopy) this.elements.shareCopy.checked = false;
    if (this.elements.shareMessage) this.elements.shareMessage.value = '';

    this.clearShareErrors();

    this.elements.shareModal.classList.add('is-open');
    document.body.classList.add('no-scroll');
  }

  closeShareModal() {
    if (!this.elements.shareModal) return;
    this.elements.shareModal.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }

  clearShareErrors() {
    this.elements.shareModal.querySelectorAll('.product-customizer__share-modal-error').forEach(el => el.remove());
  }

  showShareError(field, message) {
    this.clearShareErrors();
    const errorEl = document.createElement('p');
    errorEl.className = 'product-customizer__share-modal-error';
    errorEl.textContent = message;
    if (field && field.parentNode) {
      field.parentNode.appendChild(errorEl);
    }
  }

  async submitShareForm() {
    this.clearShareErrors();

    const emailTo = this.elements.shareEmailTo?.value?.trim();
    const emailFrom = this.elements.shareEmailFrom?.value?.trim();
    const message = this.elements.shareMessage?.value?.trim();
    const copy = this.elements.shareCopy?.checked;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailTo || !emailRegex.test(emailTo)) {
      this.showShareError(this.elements.shareEmailTo, 'Veuillez renseigner une adresse email valide.');
      return;
    }

    if (!emailFrom || !emailRegex.test(emailFrom)) {
      this.showShareError(this.elements.shareEmailFrom, 'Veuillez renseigner votre adresse email.');
      return;
    }

    const imgSrc = this.elements.previewImage?.src || '';
    if (!imgSrc || imgSrc.includes('/products/')) {
      this.showShareError(null, 'Veuillez d\'abord personnaliser votre création.');
      return;
    }

    this.elements.shareSubmit.classList.add('is-loading');
    this.elements.shareSubmit.textContent = 'Envoi en cours...';

    try {
      const apiEndpoint = this.config?.apiEndpoint || '';
      const customData = this.getCustomizationData();
      const response = await fetch(`${apiEndpoint}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailTo,
          email_from: emailFrom,
          message: message || '',
          img: imgSrc,
          product_handle: this.config?.productHandle || '',
          variant_id: this.config?.variantId || '',
          customization: {
            predesign: customData.predesign || '',
            color: customData.color || '',
            picto: customData.picto || '',
            texts: customData.texts || {}
          },
          copy: copy ? '1' : '0'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        this.elements.shareModalForm.style.display = 'none';
        this.elements.shareModalSuccess.style.display = '';
        this.elements.shareModalMsg.textContent = 'Bravo ! Votre création a bien été envoyée par mail.';
      } else {
        this.showShareError(null, data.error || 'Une erreur est survenue pendant l\'envoi du mail.');
      }
    } catch (e) {
      console.error('Share email error:', e);
      this.showShareError(null, 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      this.elements.shareSubmit.classList.remove('is-loading');
      this.elements.shareSubmit.textContent = 'Partager la maquette';
    }
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = 'product-customizer__notification';
    notification.textContent = message;
    const bg = type === 'error' ? '#c62828' : 'var(--primary-100, #002c54)';
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${bg};
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 9999;
      font-family: var(--font-family-paragraph, 'Inter', sans-serif);
      font-size: 14px;
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  }

  async saveCreation() {
    if (!this.elements.previewImage || !this.elements.previewImage.src) {
      this.showNotification('Veuillez d\'abord personnaliser votre création');
      return;
    }

    const customData = this.getCustomizationData();

    try {
      // 1) Add to cart first
      const properties = {
        '_customization_preview': this.elements.previewImage.src,
        '_customization_predesign': customData.predesign || '',
        '_customization_color': customData.color || '',
        '_customization_picto': customData.picto || '',
        ...Object.fromEntries(
          Object.entries(customData.texts || {}).filter(([k]) => k.startsWith('phrase')).map(([k, v]) => [`ligne ${k.replace('phrase', '')}`, v || ''])
        ),
        '_customization_product_id': String(customData.productId),
        '_customization_variant_id': String(customData.variantId),
        '_customization_config': JSON.stringify(customData)
      };

      const formData = {
        items: [{
          id: parseInt(this.config.variantId),
          quantity: this.quantity,
          properties
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // 2) Open drawer cart immediately
        this.showNotification('Création sauvegardée dans le panier !');
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.querySelector('side-cart')?.open?.();

        // 3) Save to configurateur API (non-blocking background)
        this._saveCustomizationAsync(customData);
      } else {
        this.showNotification('Erreur lors de la sauvegarde');
      }
    } catch (e) {
      console.error('Save creation error:', e);
      this.showNotification('Erreur lors de la sauvegarde');
    }
  }

  async checkUrlParams() {
    const params = new URLSearchParams(window.location.search);

    const editKey = params.get('edit_key');
    const cloneKey = params.get('clone_key');
    const sourceKey = editKey || cloneKey;

    if (editKey) {
      this.editKey = editKey;
    }

    const decodeHtml = (v) => {
      if (v == null) return v;
      const txt = document.createElement('textarea');
      txt.innerHTML = String(v);
      // Run twice to handle double-encoded entities (e.g. "&amp;amp;" -> "&amp;" -> "&")
      const once = txt.value;
      txt.innerHTML = once;
      return txt.value;
    };

    // Fetch customization data from the cart line to avoid URL encoding issues
    if (sourceKey) {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (res.ok) {
          const cart = await res.json();
          const item = (cart.items || []).find(i => i.key === sourceKey);
          if (item && item.properties) {
            const p = item.properties;
            this.pendingUrlParams = {
              predesign: decodeHtml(p._customization_predesign) || null,
              color: decodeHtml(p._customization_color) || null,
              picto: decodeHtml(p._customization_picto) || null,
              ...Object.fromEntries(
                Object.entries(p).filter(([k]) => k.startsWith('ligne ')).map(([k, v]) => [`phrase${k.replace('ligne ', '')}`, decodeHtml(v) || null])
              ),
              ...Object.fromEntries(
                Object.entries(p).filter(([k]) => k.startsWith('_customization_phrase')).map(([k, v]) => [k.replace('_customization_', ''), decodeHtml(v) || null])
              )
            };
          }
        }
      } catch (err) {
        console.error('[Customizer] Failed to fetch cart line for restore:', err);
      }
    }

    // Fallback: legacy URL params (backward compat)
    if (!this.pendingUrlParams && (params.has('predesign') || params.has('color') || [...params.keys()].some(k => k.startsWith('phrase')))) {
      const urlParams = {
        predesign: decodeHtml(params.get('predesign')),
        color: decodeHtml(params.get('color')),
        picto: decodeHtml(params.get('picto'))
      };
      for (const [k, v] of params.entries()) {
        if (k.startsWith('phrase')) urlParams[k] = decodeHtml(v);
      }
      this.pendingUrlParams = urlParams;
    }
  }

  async restoreFromUrlParams() {
    if (!this.pendingUrlParams) return;

    const params = this.pendingUrlParams;
    this.pendingUrlParams = null;
    const hadSavedParams = params.predesign != null
      || params.color != null
      || params.picto != null
      || Object.keys(params).some(k => k.startsWith('phrase'));

    // Gate intermediate generatePreview calls fired by selectDesign /
    // selectDefaultPicto / selectPicto — otherwise an earlier render with
    // default values can race and overwrite the final one.
    this._restoringFromParams = true;
    this.showLoading();

    try {
      // Predesign: always call selectDesign so word inputs, pictos, and
      // predesignData are set. Fall back to the first design if the saved
      // one no longer exists (renamed/removed).
      let design = null;
      if (params.predesign && this.predesigns.length) {
        design = this.predesigns.find(p => p.filename === params.predesign || p.filename.includes(params.predesign)) || null;
      }
      if (!design) design = this.predesigns[0];
      if (design) await this.selectDesign(design);

      // Color
      if (params.color) {
        const colorItem = this.elements.colorsGrid?.querySelector(`[data-color="${params.color}"]`);
        if (colorItem) {
          this.elements.colorsGrid.querySelectorAll('.product-customizer__color-item').forEach(el => {
            el.classList.remove('product-customizer__color-item--active');
          });
          colorItem.classList.add('product-customizer__color-item--active');
          this.currentColor = colorItem.dataset.color;
          this.updateColorSummary(this.currentColor);
          this.updateCurrentColorIndicator(colorItem);
          this.updateVariantFromOptions();
        }
      }

      // Picto: restore saved one, or explicitly clear if the cart line had
      // none (decodeHtml coerces empty string to null, so we cannot rely on
      // a strict empty check).
      if (params.picto) {
        const pictoItem = this.elements.pictosGrid?.querySelector(`[data-code="${params.picto}"]`);
        if (pictoItem) this.selectPicto(pictoItem);
      } else if (hadSavedParams) {
        this.clearPicto();
      }

      this.updateClearButton();

      // Texts: override the defaults applied by selectDesign. Clear inputs
      // whose saved value was empty so the restored state matches the cart.
      const inputs = this.elements.wordsContainer.querySelectorAll('.product-customizer__word-input');
      inputs.forEach((input) => {
        const lineId = input.name;
        if (!lineId) return;
        if (Object.prototype.hasOwnProperty.call(params, lineId)) {
          input.value = params[lineId] || '';
        }
      });
      this.updateClearButton();
    } finally {
      this._restoringFromParams = false;
    }

    // Single preview with the fully-restored state.
    await this.generatePreview();

    // Clean URL but preserve edit_key if present
    const url = new URL(window.location.href);
    const editKey = url.searchParams.get('edit_key');
    const variant = url.searchParams.get('variant');
    const cleanUrl = new URL(window.location.pathname, window.location.origin);
    if (variant) {
      cleanUrl.searchParams.set('variant', variant);
    }
    if (editKey) {
      cleanUrl.searchParams.set('edit_key', editKey);
    }
    window.history.replaceState({}, '', cleanUrl.toString() + '#product-customizer');

    // Scroll to customizer section
    const section = document.getElementById('product-customizer');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  saveWordsOrder() {
    if (!this.elements?.wordsContainer) return;
    this.savedWordsOrder = Array.from(
      this.elements.wordsContainer.querySelectorAll(".product-customizer__word-row")
    ).map((row) => row.dataset.lineId);
  }
  
  restoreWordsOrder() {
    const container = this.elements?.wordsContainer;
    if (!container || !this.savedWordsOrder?.length) return;
  
    const rows = Array.from(container.querySelectorAll(".product-customizer__word-row"));
    const map = new Map(rows.map((r) => [r.dataset.lineId, r]));
  
    const ordered = this.savedWordsOrder.map((id) => map.get(id)).filter(Boolean);
    rows.forEach((r) => {
      if (!ordered.includes(r)) ordered.push(r);
    });
  
    ordered.forEach((r) => container.appendChild(r));
    this.updateWordNumbers();
  }
  
}

// Initialize with multiple fallback strategies
function initCustomizer() {
  const container = document.querySelector('.product-customizer');
  if (!container) return;
  if (container.dataset.customizerInit) return;
  container.dataset.customizerInit = 'true';
  try {
    new ProductCustomizer(container);
  } catch (e) {
    console.error('[Customizer] Init error:', e);
  }
}

// Strategy 1: defer script runs after HTML parse
if (document.readyState !== 'loading') {
  initCustomizer();
}
// Strategy 2: DOMContentLoaded fallback
document.addEventListener('DOMContentLoaded', initCustomizer);
// Strategy 3: window load fallback (last resort)
window.addEventListener('load', initCustomizer);

// Shopify theme editor support
document.addEventListener('shopify:section:load', (e) => {
  const container = e.target.querySelector('.product-customizer');
  if (container) {
    new ProductCustomizer(container);
  }
});
